<template>
  <div>
    <v-card
        v-if="!dataFetched"
        flat
        class="container--fluid"
    >
      <ApplicationLoader/>
    </v-card>
    <v-card
        v-if="dataFetched"
        flat
        class="container--fluid"
    >
      <v-card
          flat
          class="container--fluid bg-gray no-margins elevation-1 not-rounded"
      >
        <v-row class="ma-0">
          <v-col
              class="pa-6 pb-0"
              cols="7"
          >
            <v-row class="ma-0">

              <div v-if="$route && $route.params && $route.params.id > 0">
                <h4>
                  Lärlingsanmälan nr. {{ $route.params.id || '' }}
                  <span
                      class="ml-3"
                      style="font-size: 12px; text-transform: none; font-weight: normal"
                  >
                    Anmälan skapad {{ selectedApplication.createdAt | date }}
                  </span>
                </h4>
                <h6 class="mt-n3">
                  <router-link :to="'/people/' + selectedApplication.personId">
                    {{ fullname || '' }} ({{ selectedApplication.personalNumber | maskedPersonalNumber }})
                  </router-link>
                </h6>
              </div>

              <div cols="10" v-else>
                <h4>Ny lärlingsanmälan</h4>
              </div>

              <v-spacer></v-spacer>
            </v-row>
          </v-col>
          <v-col
              cols="5"
              class="pa-7 pb-0"
          >
            <v-card-text class="mt-n2 pa-0 d-flex flex-column align-end">
              <div>
                <v-btn class="mr-2 mt-2" :disabled="!canUpdateApplication || !canApprove || !approvedRegion" @click="approveApplication">
                  <v-icon>$sl-like</v-icon>
                  <span class="d-xs-none d-sm-none d-md-none d-lg-flex">Godkänn</span>
                  <span class="d-lg-none d-xs-flex">Godk.</span>
                </v-btn>

                <v-menu offset-y>
                  <template v-slot:activator="{on, attrs}">
                    <v-btn class="mr-2 mt-2" v-bind="attrs" v-on="on" :disabled="!canUpdateApplication || !approvedRegion">
                      <v-icon>$sl-arrow-down</v-icon>
                      <span class="d-xs-none d-sm-none d-md-none d-lg-none d-xl-flex">Andra Åtgärder</span>
                      <span class="d-xl-none d-xs-flex">Åtgärder</span>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item class="pa-0">
                      <v-list-item-title class="pa-0">
                        <v-btn
                            class="pa-0 ml-1"
                            text
                            @click="refuseApplication"
                            :disabled="!canDeny"
                        >
                          <v-icon class="mr-4">$sl-dislike</v-icon>
                          Neka
                        </v-btn>
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        @click="regretGU"
                        :disabled="!canRegretGU"
                    >
                      <v-list-item-title class="pa-0">
                        Ångra Lärlingsanmälan
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        @click="regretKTID"
                        :disabled="!canRegretKTID"
                    >
                      <v-list-item-title>Ångra Kvalificeringstid</v-list-item-title>
                    </v-list-item>
                    <v-list-item class="pa-0">
                      <v-list-item-title class="pa-0">
                        <v-btn
                            class="pa-0 ml-1"
                            text
                            @click="deleteApplication"
                            :disabled="isRegionary"
                        >
                          <v-icon class="mr-4">$sl-delete</v-icon>
                          Radera
                        </v-btn>
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <v-badge :value="personHasNotes" :content="'!'" overlap>
                  <v-btn class="mt-2" outlined @click="showNotes=true"
                    :disabled="!(($route && $route.params && $route.params.id > 0) && (selectedApplication && selectedApplication.personId)) || !approvedRegion">
                    <v-icon>$sl-message</v-icon>
                    <span class="d-xs-none d-sm-none d-md-none d-lg-flex">Anteckningar</span>
                    <span class="d-lg-none d-xs-flex">Ant.</span>
                  </v-btn>
                </v-badge>
              </div>
              <p class="pt-2 mb-0 bold text-right">
                {{ message }}
              </p>
            </v-card-text>
          </v-col>
        </v-row>
      </v-card>
      <div class="d-flex justify-end pa-4">
        <v-btn
            text
            v-if="showExpandButton || expansionPanelToggle.length"
            @click="showExpandButton = false; expansionPanelToggle = []"
        >
          <v-icon class="mr-4">$sl-move-expand-vertical</v-icon>
          STÄNG ALLA PANELER
        </v-btn>
        <!-- <v-btn text v-else @click="expansionPanelToggle = []">
          <v-icon class="mr-4">$sl-move-shrink-vertical</v-icon>STÄNG ALLA PANELER
        </v-btn> -->
      </div>
      <div class="pa-6 pt-0">
        <MessageBox
            type="alert"
            v-if="personHasNotes"
        >
          <h6>DET FINNS ANTECKNINGAR KOPPLADE TILL DETTA ÄRENDE</h6>
          <p>
            Klicka på knappen “anteckningar” för att visa de anteckningar som hör till detta ärende.
          </p>
        </MessageBox>
      </div>
      <v-expansion-panels
          v-model="expansionPanelToggle"
          multiple
          class="pa-6 pt-0"
      >
        <ApplicationStatus @saved="fetchData" :editable="canUpdateApplication"/>
        <ApplicationApprentice :editMode="newApplication" :editable="canUpdateApplication" @saved="fetchData"/>
        <ApplicationCompany @saved="fetchData" :editable="canUpdateApplication"/>
        <ApplicationProfession
            v-if="applicationCompany"
            @saved="fetchData"
            :editable="canUpdateApplication"
        />
        <ApplicationDocuments @saved="fetchData" :editable="canUpdateApplication" />
        <ApplicationEducation
            @saved="fetchData"
            @refuse="refuseApplication"
            :editable="canUpdateApplication"
            @get-notes="personNotes = $event"
        />
        <ApplicationDistanceEducation 
          :editable="canUpdateApplication" 
          @saved="fetchData()" 
          v-if="!selectedApplication.transferOldProfession" 
        />
        <ApplicationEvents v-if="selectedApplication.notifications"/>
        <ApplicationMessages v-if="selectedApplication.notifications"/>
      </v-expansion-panels>

      <DeleteApplicationModal
          :show="showDeleteMessage"
          @close="closeDeleteMessage"
          @send="doDeleteApplication"
      />

      <RefuseApplicationModal
          :show="showRefuseMessage"
          @close="closeRefuseMessage"
          @send="doRefuseApplication"
      />

      <ApplicationNotesModal
          v-if="selectedApplication"
          :show="showNotes"
          @new="newNote"
          @close="showNotes=false"
          :applicationId="selectedApplication.id"
          :personId="get(selectedApplication, 'personId', null)"
          :comments="comments"
      />

      <NotesEditModal
          v-if="selectedApplication"
          v-model="notesModal"
          @saved="showNotes=true"
          :applicationId="selectedApplication.id"
          :personId="selectedApplication.personId"
      />
    </v-card>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import API from '@/services/api.js'
import pick from 'lodash/pick'
import find from 'lodash/find'
import get from 'lodash/get'
import findIndex from 'lodash/findIndex'
import {RulesMixin, ApplicationValidation, OrderingErrorHandler, CanIDo} from '@/components/mixins'
import SugarDate from 'sugar'

import ApplicationLoader from '@/components/application/ApplicationLoader.vue'
import ApplicationStatus from '@/components/application/ApplicationStatus/ApplicationStatus.vue'
import ApplicationApprentice from '@/components/application/ApplicationApprentice/ApplicationApprentice.vue'
import ApplicationCompany from '@/components/application/ApplicationCompany/ApplicationCompany.vue'
import ApplicationProfession from '@/components/application/ApplicationProfession/ApplicationProfession.vue'
import ApplicationDocuments from '@/components/application/ApplicationDocuments/ApplicationDocuments'
import ApplicationEducation from '@/components/application/ApplicationEducation/ApplicationEducation'
import ApplicationDistanceEducation
  from '@/components/application/ApplicationDistanceEducation/ApplicationDistanceEducation'
import ApplicationEvents from '@/components/application/ApplicationEvents/ApplicationEvents'
import ApplicationMessages from '@/components/application/ApplicationMessages/ApplicationMessages'
import DeleteApplicationModal from '@/components/application/DeleteApplicationModal.vue'
import RefuseApplicationModal from '@/components/application/RefuseApplicationModal.vue'
import ApplicationStateLogs from '@/components/application/ApplicationStateLogs.vue'
import ApplicationNotesModal from '@/components/application/ApplicationNotesModal.vue'
import NotesEditModal from '@/components/NotesEditModal.vue'
import MessageBox from '@/components/common/MessageBox.vue'
import flatMap from 'lodash/flatMap'

// @group Application
// Application component
export default {
  name: 'Application',

  mixins: [RulesMixin, ApplicationValidation, OrderingErrorHandler, CanIDo],

  data: () => ({
    dataFetched: false,
    notesModal: false,
    showNotes: false,
    application: {},
    employment: {},
    showOrder: false,
    courses: {
      Inriktning: [],
      Gemensamma: [],
      Programgemensamma: [],
      Fördjupning: []
    },
    showDeleteMessage: false,
    states: [],
    showRefuseMessage: false,
    expansionPanelToggle: [],
    newApplication: false,
    showExpandButton: true,
    personNotes: []
  }),

  components: {
    ApplicationStatus,
    ApplicationApprentice,
    ApplicationCompany,
    ApplicationProfession,
    RefuseApplicationModal,
    DeleteApplicationModal,
    ApplicationDistanceEducation,
    ApplicationEducation,
    ApplicationDocuments,
    ApplicationEvents,
    ApplicationLoader,
    ApplicationStateLogs,
    ApplicationNotesModal,
    NotesEditModal,
    MessageBox,
    ApplicationMessages
  },
  filters: {
    filterGrade(g) {
      if (g && typeof g === 'string') {
        return g.toUpperCase()
      }
      return ''
    },
    maskedPersonalNumber(p) {
      if (p && p.length > 8) return p.substring(0, 8) + '-****'
      return ''
    }
  },
  created() {
    console.log('Application - created')
    this.fetchData()
    this.expansionPanelToggle = [0, 1, 2, 3, 4, 5, 6, 7, 8]
  },

  watch: {
    $route: 'fetchData',
    selectedApplication: {
      handler() {
        if (this.selectedApplication && this.selectedApplication.id) {
          this.approvedStateByCompany ? (this.showOrder = true) : (this.showOrder = false)
        }
      },
      deep: true
    }
    //- Make sure any changes in vuex state is reflected in this.state
  },
  computed: {
    ...mapGetters('application', [
      'selectedApplication',
      'applicationOrders',
      'applicationCompany',
      'applicationProfession',
      'applicationEmployee',
      'approvedStateByCompany'
    ]),
    ...mapGetters('currentuser', ['currentUser']),
    ...mapGetters('basic', [
      'basicApplicationStates',
      'confirmedAndApprovedByCompany',
      'approvedState',
      'approvedByByn',
      'getProfessionById',
      'orderedState',
      'closedState',
      'approvedDistanceOrderState',
      'orderedDistanceOrderState',
      'guIssuedState'
    ]),

    comments() {
      const distanceOrders = get(this, 'selectedApplication.belongsToPerson.distanceOrders', [])
      if ( distanceOrders.length )
        return flatMap(distanceOrders, 'comments')

      return []
    },

    personHasNotes() {
      let hasNotes = false
      const personNotes = get(this, 'personNotes', []).length
      const applicationBelongsToPersonNotes = get(this, 'selectedApplication.belongsToPerson.notes', []).length
      const applicationNotes = get(this, 'selectedApplication.notes', []).length
      const orderComments = this.comments?.length

      if ( personNotes )
        hasNotes = true

      hasNotes = Boolean(applicationBelongsToPersonNotes || orderComments || applicationNotes || personNotes)

      return hasNotes
    },

    canRegret() {
      return this.canRegretGU || this.canRegretKTID
    },

    isRegionary() {
      return this.currentUser.userrole.isRegionary;
    },

    canRegretKTID() {
      const isClosed = get(this, 'selectedApplication.stateId', null) === get(this, 'closedState.id', null)
      const approvedBEC = get(this, 'selectedApplication.education.basicEducationCertificate.approved', false)
      return isClosed && approvedBEC
    },
    canRegretGU() {
      const isApproved = get(this, 'selectedApplication.stateId', null) === get(this, 'approvedState.id', null)
      const isClosed = get(this, 'selectedApplication.stateId', null) === get(this, 'closedState.id', null)
      const distanceOrders = get(this, 'applicationOrders', [])
      const sentOrders = find(distanceOrders, ['stateId', get(this, 'orderedDistanceOrderState.id', null)])
      const distanceOrderApprovedByCompany = find(distanceOrders, ['applicationId', get(this, 'selectedApplication.id', null)])?.approvedByCompanyAt

      let boolean = true
      
      if ( isApproved || (isClosed && !sentOrders && distanceOrders.length > 0) )
        boolean = isApproved || (isClosed && !sentOrders && distanceOrders.length > 0)
      
      if ( distanceOrderApprovedByCompany )
        boolean = false

      return boolean
    },

    showDocumectsAndCoursesFeature() {
      if (process.env.VUE_APP_DOCUMENTS_COURSES_SHOW) {
        return Boolean(process.env.VUE_APP_DOCUMENTS_COURSES_SHOW === 'true')
      }
      return false
    },

    // @vuese
    //- Used to return full name
    fullname() {
      var ret = ''
      if (this.selectedApplication.firstName) ret = this.selectedApplication.firstName + ' '
      if (this.selectedApplication.lastName) ret += this.selectedApplication.lastName

      return ret
    },

    refusedPreviousStateId() {
      if (this.selectedApplication) {
        // If approved by regionay one
        if ([this.orderedState.id, this.guIssuedState.id].includes(this.selectedApplication.stateId)) {
          const state = find(this.basicApplicationStates, {
            state: 'confirmedAndApprovedByCompany',
            initiatedByCompany: Boolean(this.selectedApplication.isCompanyApplication)
          })
          return state && state.id ? state.id : -1
        }
        // Else send back to draft
        return this.draftStateId
      }
      return -1
    },
    // @vuese
    //- Used to return draft state id
    draftStateId() {
      if (this.selectedApplication) {
        const state = find(this.basicApplicationStates, {
          state: 'draft',
          initiatedByCompany: Boolean(this.selectedApplication.isCompanyApplication)
        })
        return state && state.id ? state.id : -1
      }
      return -1
    },
    // @vuese
    //- Used to return company draft state id
    companyDraftStateId() {
      const state = find(this.basicApplicationStates, {state: 'draft', initiatedByCompany: true})
      return state && state.id ? state.id : -1
    },
    // @vuese
    //- Used to return company draft state id
    companyDraftState() {
      const state = find(this.basicApplicationStates, {state: 'draft', initiatedByCompany: true})
      return state && state.id ? state : {}
    },
    // @vuese
    //- Used to return apprentice draft state id
    apprenticeDraftStateId() {
      const state = find(this.basicApplicationStates, {state: 'draft', initiatedByCompany: false})
      return state && state.id ? state.id : -1
    },
    // @vuese
    //- Used to return apprentice draft state
    apprenticeDraftState() {
      const state = find(this.basicApplicationStates, {state: 'draft', initiatedByCompany: false})
      return state && state.id ? state : {}
    },
    // @vuese
    //- Used to return signed state id
    signedStateId() {
      const state = find(this.basicApplicationStates, {state: 'signed'})
      return state && state.id ? state.id : -1
    },
    // @vuese
    //- Used to check if user has persmission to edit the application.
    canEdit() {
      var ret = true
      if (this.approvedRegion == false) {
        return false
      } else if (this.currentUser.userrole.isRegionary == true && this.approvedStateByCompany == false) {
        return false
      }

      return ret
    },

    // @vuese
    //- Used to check if user has persmission to approve the application.
    canApprove() {
      return Boolean(
          get(this, 'selectedApplication.stateId', null) === get(this, 'orderedState.id', null) &&
          this.isSameApprovedBy == false &&
          this.approvedRegion == true
      )
    },

    // @vuese
    //- Used to check if user has persmission to deny the application.
    canDeny() {
      return Boolean(
          this.canApprove ||
          this.confirmedAndApprovedByCompany
      )
    },

    // @vuese
    //- Used to return approved state by Byn
    approvedStateByByn() {
      if (this.approvedByByn) {
        return this.approvedByByn.includes(this.selectedApplication.stateId)
      }
    },

    // // @vuese
    // //- Used to return if the currentUser's is regionary and check if it is the same regionary approved the application
    isSameApprovedBy() {
      return Boolean(
          get(this, 'selectedApplication.stateId', null) == get(this, 'orderedState.id', null) &&
          get(this, 'currentUser.id', null) == get(this, 'selectedApplication.orderedBy.id', null)
      )
    },

    // @vuese
    //- Used to return if the currentUser's is regionary and check the RegionId is the same as the application's RegionId for the company
    approvedRegion() {
      const applicationRegion = get(this, 'selectedApplication.regionId', null)
      const applicationWorkplaceId = get(this, 'selectedApplication.workplaceId', null)
      const companyWorkplaces = get(this, 'applicationCompany.workplaces', [])
      const applicationWorkplace = find(companyWorkplaces, (c) => c.id === applicationWorkplaceId)

      //- Regionarys can only edit applications on regions to which they are tied
      if (get(this, 'currentUser.userrole.isRegionary', true) == true) {
        return Boolean(
            find(this.currentUser.userRegions, (r) =>
                r.regionId === get(applicationWorkplace, 'regionId', null) ||
                r.regionId === get(this, 'applicationCompany.regionId', null) ||
                r.regionId === applicationRegion
            )
        )
      }
      return true
    },

    message() {
      //TODO: add conditions for this messages
      if (this.expansionPanelToggle.length === 0)
        return 'Det går inte att ändra en anmälan utan att visa den först. Tryck på “öppna alla paneler” för att visa denna anmälan.'
      if (!this.canApprove && this.selectedApplication.stateId == this.orderedState.id)
        return 'Obs! Du har redan godkänt den här anmälan. Det krävs att en annan regionär kontragodkänner för att anmälan ska godkännas fullständigt.'
      return ''
    }
  },

  asyncComputed: {
    canUpdateApplication(){
      return this.canIDo('application', 'update');
    },
  },

  methods: {

    get,

    ...mapActions('application', [
      'setSelectedApplication',
      'updateSelectedApplication',
      'setApplicationCompany',
      'setApplicationProfession',
      'setApplicationEmployee',
      'setApplicationCompanyEmployments',
      'setApplicationOrders',
      'setApplicationDistanceEducations',
      'setApplicationWorkplace',
      'setApplicationDepartments',
      'setApplicationDepartment'
    ]),

    getOrders() {
      return API.listDistanceOrders({
        startAt: 0,
        pageSize: 50,
        advSearch: {applicationId: this.selectedApplication.id}
      }).then(({data}) => {
        this.setApplicationOrders(data.rows)
      })
    },

    async regretGU() {
      const res = await this.$dialog.confirm({
        text: 'Vill du ångra den godkända distansutbilningen och utfärdandet av e-bok?',
        title: 'Ångra Grundutbildning',
        actions: [
          {text: 'Avbryt', key: false, icon: '$sl-cancel'},
          {text: 'Ångra', key: true, icon: '$sl-undo', outlined: true}
        ]
      })

      if (res !== true) return

      API.changeStateApplication(this.selectedApplication.id, {
        data: {stateId: get(this, 'orderedState.id', -1), isRejection: true}
      })
          .then(res => {
            this.fetchData()
          })
          .catch(e => {
            this.$events.fire('showError', `Kunde inte ångra... ${e.message}`)
          })
    },

    async regretKTID() {
      const res = await this.$dialog.confirm({
        text: 'Vill du ångra den godkända grundutbilningen?',
        title: 'Ångra Kvalificeringstid',
        actions: [
          {text: 'Avbryt', key: false, icon: '$sl-cancel'},
          {text: 'Ångra', key: true, icon: '$sl-undo', outlined: true}
        ]
      })

      if (res !== true) return

      const hasOrders = Boolean(get(this, 'applicationOrders', []).length)
      const education = get(this, 'selectedApplication.education', null)

      if (hasOrders && education) {
        API.denyEducation(education.id, {data: {message: 'Ångrad'}})
            .then(res => {
              this.fetchData()
            })
            .catch(e => {
              this.$events.fire('showError', `Kunde inte ångra... ${e.message}`)
            })
      } else {
        API.changeStateApplication(this.selectedApplication.id, {
          data: {
            stateId: get(this, 'confirmedAndApprovedByCompany', [-1, -1])[Boolean(this.isCompanyApplication) | 0],
            isRejection: true
          }
        })
            .then(res => {
              this.fetchData()
            })
            .catch(e => {
              this.$events.fire('showError', `Kunde inte ångra... ${e.message}`)
            })
      }
    },

    newNote() {
      this.showNotes = false
      this.notesModal = true
    },

    /**
     * @vuese
     * Used fetch the company by id
     * @arg The argument is an integer value representing company id
     */
    getCompany(id) {
      API.getCompany(id)
          .then(response => {
            this.company = response.data
            var comp = find(this.company.workplaces, c => {
              return c.id === this.selectedApplication.workplaceId
            })
          })
          .catch(error => {
            console.log('get getCompany', error.response)
          })
    },

    getDistanceOrderCourses() {
      this.setApplicationDistanceEducations([])
      return API.listDistanceOrderCourses(this.selectedApplication.professionId, this.selectedApplication.id).then(
          ({data}) => {
            this.setApplicationDistanceEducations(data)
          }
      )
    },

    getCompany() {
      this.setApplicationCompany(null)
      return API.getCompany(this.selectedApplication.companyId).then(({data}) => {
        this.setApplicationCompany(data)
      })
    },
    getWorkplaces() {
      this.setApplicationWorkplace([])
      return API.getWorkplace(this.selectedApplication.companyId, this.selectedApplication.workplaceId).then(
          ({data}) => {
            this.setApplicationWorkplace(data)
          }
      )
    },
    getEmployee() {
      this.setApplicationEmployee(null)
      return API.getEmployment(this.selectedApplication.instructorId).then(({data}) => {
        API.getPerson(data.personId).then(({data}) => {
          this.setApplicationEmployee(data)
        })
      })
    },
    getDepartments() {
      this.setApplicationDepartments([])
      API.listDepartments(this.selectedApplication.companyId).then(({data}) => {
        this.setApplicationDepartments(data)
        if (this.selectedApplication.departmentId) {
          const department = data.find(dep => dep.id === this.selectedApplication.departmentId)
          this.setApplicationDepartment(department || {})
        }
      })
    },
    getApplicationCompanyEmployments() {
      function numApprentice(arr) {
        let res = 0
        if (arr) res = arr.length
        if (res <= 0) {
          return `${res} lärling`
        }
        return `${res} lärlingar`
      }

      this.setApplicationCompanyEmployments([])
      API.listCompanyEmployments(this.selectedApplication.companyId).then(({data}) => {
        const employments = data.map(o => {
          return {
            id: o.id,
            employeeId: o.employed.id,
            employed: o.employed,
            fullName: o.employed.firstName + ' ' + o.employed.lastName + ' (' + numApprentice(o.apprentices) + ')',
            tradeCertificates: o.employed.tradeCertificates,
            machinequalifications: o.employed.machinequalifications,
            mobilephone: o.employed.mobilephone,
            email: o.employed.email,
            profession: o.profession,
            exemtions: o.employed.instructorExemptions
          }
        })

        this.setApplicationCompanyEmployments(employments)
      })
    },
    fetchData() {
      // @vuese
      //- get application data
      if (this.$route.params.id) {
        console.log('Fetching existing application: ', this.$route.params.id)
        this.dataFetched = false
        this.setSelectedApplication(null)
        API.getApplication(this.$route.params.id)
            .then(res => {
              this.setSelectedApplication(res.data)
              const dataToFetch = [this.getOrders()]
              if (this.selectedApplication.professionId) {
                const profession = this.getProfessionById(this.selectedApplication.professionId)
                this.setApplicationProfession(profession)
                dataToFetch.push(this.getDistanceOrderCourses())
              } else {
                this.setApplicationProfession(null)
                this.setApplicationDistanceEducations([])
              }

              if (this.selectedApplication.companyId) {
                dataToFetch.push(this.getCompany())
                dataToFetch.push(this.getDepartments())
                dataToFetch.push(this.getApplicationCompanyEmployments())
              } else {
                this.company = null
                this.setApplicationCompany(null)
                this.setApplicationDepartments([])
                this.setApplicationCompanyEmployments([])
              }

              if (this.selectedApplication.companyId && this.selectedApplication.workplaceId) {
                dataToFetch.push(this.getWorkplaces())
              } else {
                this.setApplicationWorkplace(null)
              }

              if (this.selectedApplication.instructorId) {
                dataToFetch.push(this.getEmployee())
              } else {
                this.setApplicationEmployee(null)
              }

              Promise.all(dataToFetch)
                  .then(() => {
                    console.log('All data fetched!')
                  })
                  .catch(err => {
                    console.log('Data fetchec with errors', err)
                  })
                  .finally(() => {
                    this.dataFetched = true
                  })
            })
            .catch(error => {
              console.log('There was an error: ', error)
            })
      } else {
        console.log('New application')
        // @vuese
        //- Set empty template
        this.setSelectedApplication({
          firstName: '',
          lastName: '',
          personalNumber: '',
          email: '',
          address1: '',
          address2: '',
          city: '',
          zip: '',
          country: 'SE',
          company: {},
          companyId: null,
          instructorId: 0,
          professionId: 0,
          mobilephone: '',
          employmentDate: '',
          state: this.companyDraftState,
          applicationDocuments: [],
          isCompanyApplication: true,
          stateId: this.companyDraftStateId
        })

        this.setApplicationProfession(null)
        this.setApplicationDistanceEducations([])
        this.company = null
        this.setApplicationCompany(null)
        this.setApplicationDepartments([])
        this.setApplicationCompanyEmployments([])
        this.setApplicationWorkplace(null)
        this.setApplicationEmployee(null)

        // Open apprentice tab by default for new application
        this.expansionPanelToggle = [1]
        this.newApplication = true
        this.dataFetched = true
      }
    },

    closeDeleteMessage() {
      this.showDeleteMessage = false
    },

    closeRefuseMessage() {
      this.showRefuseMessage = false
    },

    /**
     * @vuese
     * Used to approve State Application
     * @arg The argument is integer values representing selected application id
     */
    async approveApplication() {
      console.log('Application - approveApplication')
      if (!this.selectedApplication && !this.selectedApplication.id && !this.selectedApplication.stateId) {
        this.$events.fire('showError', 'Ingen anmälan angiven, testa att ladda om sidan.')
        return
      }
      if (!this.isValidStateChange(this.selectedApplication.stateId, this.selectedApplication)) {
        this.$events.fire(
            'showError',
            'Du behöver komplettera anmälan med mer information för att kunna byta till vald status.'
        )
        return
      }

      const today = new Date().setDate(new Date().getDate() - 1)

      if( SugarDate.Date.isBefore(new Date(this.selectedApplication?.employmentDate), today) ) {
        const res = await this.$dialog.confirm({
          title: 'Godkänn anmälan',
          text: 'Observera att startdatum för denna anmälan ligger före dagens datum. För att godkänna denna anmälan, bekräfta att ni är medvetna om detta.',
          actions: [
            {text: 'Avbryt', key: false, icon: '$sl-cancel', outlined: true},
            {text: 'Godkänn', key: true, icon: '$sl-like'}
          ]
        })

        if (res !== true) {
          return
        }
      } else {
        const res = await this.$dialog.confirm({
          title: 'Vill du godkänna denna anmälan?',
          text: '',
          width: '530',
          actions: [
            {text: 'Avbryt', key: false, icon: '$sl-cancel', outlined: true},
            {text: 'Godkänn', key: true, icon: '$sl-like'}
          ]
        })

        if (res !== true) {
          return
        }
      }

      const hasCourses = get(this, 'applicationProfession.distanceEducationProvider.features.hasCourses',true)

      API.changeStateApplication(this.selectedApplication.id, {
        data: {stateId: Number(this.approvedByByn[0])}
      })
          .then(response => {
            if (response && response.data) {
              //- If dep has courses we get approved, if not closed
              const afterStateId = hasCourses ? this.approvedByByn[0] : this.closedState.id

              this.updateSelectedApplication({
                stateId: Number(afterStateId)
              })
            }
          })
          .catch(error => {
            console.log('There was an error:', error)
            return this.handleOrderingErrors(error)
          })
    },

    refuseApplication() {
      console.log('Application - refuseApplication')
      this.showRefuseMessage = true
    },

    /**
     * @vuese
     * Used to refuse State Application
     * @arg The argument is integer values representing selected application id
     */
    doRefuseApplication(messageOpts) {
      console.log('Application - doRefuseApplication')
      this.showRefuseMessage = false

      //- Add recipients to messageOpts
      if (messageOpts.sendMessage && this.selectedApplication.email) {
        messageOpts.recipients = []
        messageOpts.recipients.push(pick(this.selectedApplication, ['firstName', 'lastName', 'email']))
      } else {
        messageOpts.sendMessage = false
      }

      let notification = messageOpts.sendMessage ? {text: messageOpts.message} : null

      API.changeStateApplication(this.selectedApplication.id, {
        data: {stateId: this.refusedPreviousStateId, notification}
      })
          .then(response => {
            if (messageOpts.sendMessage) {
              API.sendMessage({data: messageOpts}).then(reponse => {
              })
            }
            this.fetchData()
            //this.updateSelectedApplication({stateId: this.refusedPreviousStateId})
          })
          .catch(error => {
            console.log('There was an error:', error)
            return this.handleOrderingErrors(error)
          })
    },

    /**
     * @vuese
     * Used to close modal
     */
    deleteApplication() {
      if (!(this.selectedApplication && this.selectedApplication.id)) {
        this.$events.fire('showError', 'Anmälan är inte sparad ännu!')
        return
      }

      this.showDeleteMessage = true
    },

    /**
     * @vuese
     * Used to delete Application
     * @arg The argument is integer values representing selected application id
     */
    doDeleteApplication(messageOpts) {
      console.log('Application - doDeleteApplication')

      //- Add recipients to messageOpts
      if (messageOpts.sendMessage && this.selectedApplication.email) {
        messageOpts.recipients = []
        messageOpts.recipients.push(pick(this.selectedApplication, ['firstName', 'lastName', 'email']))
      } else {
        messageOpts.sendMessage = false
      }

      API.deleteApplication(this.selectedApplication.id)
          .then(response => {
            if (messageOpts.sendMessage) {
              API.sendMessage({data: messageOpts}).then(reponse => {
              })
            }
            this.$nextTick(
                () => this.$router.push({name: 'Applications'}),
                this.$events.fire('showInfo', 'Anmälan raderad.')
            )
          })
          .catch(error => {
            console.log('There was an error:', error.response)
          })
    }
  }
}
</script>

<style scoped>
.mainButton {
  width: 100%;
}

.date_time {
  font-size: 18px;
  font-weight: 300;
  font-family: 'Dosis', sans-serif;
}

.notification_title {
  font-size: 18px;
  font-weight: 600;
  font-family: 'Dosis', sans-serif;
  color: rgb(0, 0, 0);
}
</style>
